import { capitalize } from '@attain-apps/string-utils'

import type { AlreadyExistsError } from './already-exists-error'
import type { NotFoundError } from './not-found-error'
import type { NotImplementedError } from './not-implemented-error'
import { UserFriendlyError } from './user-friendly-error'
import type { ValidationError } from './validation-error'

/**
 * Enables type-checking that is not affected by having multiple versions of
 * this library installed
 */
// The string here is used as the type description for the type checker
const MARKER_SYMBOL = Symbol.for('@attain-apps/FailedOperationError')

/**
 * Request failed for some reason
 *
 * Should only be used when other errors are not more appropriate
 *
 * @see {@link AlreadyExistsError}, {@link NotFoundError},
 * {@link NotImplementedError}, {@link ValidationError}
 * @example
 * try {
 *   await createUser(info)
 * } catch (err) {
 *   throw new FailedOperationError({ description: 'create user' }, err)
 *   // FailedOperationError: Failed to create user
 * }
 */
export class FailedOperationError extends UserFriendlyError {
  // NOTE: must be all upper case to avoid naming collision with the class name
  public static override readonly NAME: string = 'FailedOperationError'
  public override readonly name: string = FailedOperationError.NAME

  // Used by TypeChecker
  private readonly [MARKER_SYMBOL] = true as const

  constructor(
    {
      description = 'execute request',
      reason,
    }: FailedOperationError.constructor.Options = {},
    actualError?: Error,
  ) {
    super(
      `Failed to ${description}${reason ? `. ${capitalize(reason)}` : ''}`,
      actualError,
    )
  }
}
export namespace FailedOperationError {
  export namespace constructor {
    export interface Options {
      /**
       * Description of the action that failed
       *
       * @default 'execute request'
       */
      description?: string
      /**
       * Explanation on why the action failed
       *
       * @example
       * 'Too many requests in a short period of time'
       * 'Unexpected error. Wait a little bit and try again'
       */
      reason?: string
    }
  }

  // NOTE: not using @attain-apps/type-guards as that would introduce circular imports
  export function isFailedOperationError(
    val: unknown,
  ): val is FailedOperationError {
    return !!(val as FailedOperationError)?.[MARKER_SYMBOL]
  }
}
